import { _ as _applyDecoratedDescriptor, a as _initializerDefineProperty } from '../_rollupPluginBabelHelpers-3b0ba74f.js';
import Service, { inject } from '@ember/service';
import { set } from '@ember/object';
import { withoutEmptyBrackets } from '../lib/invoice-line-name.js';
import { getOwner } from '@ember/application';
import { restartableTask, task, dropTask, timeout } from 'ember-concurrency';
import { beebeeAjaxGet } from '../lib/utility/ajax.js';
import '@ember/utils';
import { safeTimeZone } from '../lib/utility/dates.js';
import { setDefaultCustomField } from '../lib/utility/models.js';
import '../lib/utility/province.js';
import moment from 'moment-timezone';
import { notifyUserError } from '../lib/utility/misc.js';
import { singularize } from 'ember-inflector';
import getConfig from '../lib/get-config.js';
import { pickInvoiceTaxes } from '../lib/invoice-taxes.js';

var _class, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5, _descriptor6, _descriptor7;

// ============================================================================
// CONSTANTS
// ============================================================================

/** UI stabilization delay (ms) */
const UI_STABILIZATION_DELAY = 200;

/** Default currency */
const DEFAULT_CURRENCY = 'EUR';

/** Default XML document type */
const DEFAULT_XML_DOCUMENT_TYPE = 'TD01: Fattura';

/** Default expense IVA rate */
const DEFAULT_EXPENSE_IVA_RATE = '22';

/** Models that carry a status */
const MODELS_WITH_STATUS = Object.freeze(['contact', 'product', 'project', 'workhour']);

/** Document-type -> default status */
const DOCUMENT_TYPE_STATUS_MAP = Object.freeze({
  ddt: 'active',
  receipt: 'unpaied',
  contract: 'open',
  default: 'draft'
});

/** Document types supporting the advanced option */
const DOCUMENT_TYPES_WITH_ADVANCED = Object.freeze(['estimate', 'contract']);

// ============================================================================
// SERVICE
// ============================================================================

/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      service — Octane replacement for the Classic `new-model` mixin;
 *              creates new records (contact/invoice/event/expense/…) with the
 *              org's default fiscal, status and custom-field configuration
 *   extends:   Service (ember, base class)
 *   template:  none
 *   key:       createNewModel (@dropTask, main entry / mirrors mixin's showNew),
 *              showNew, completeTypeInvoice (@restartableTask), invNumber
 *              (@restartableTask), applyInvoiceUser (@task — re-points a draft
 *              at the emitter the backend returned), emitterRitenutaDefault
 *              (@task — ritenuta default for backend-generated lines),
 *              getDocumentType/getInvoiceLordo/
 *              getInvoiceAdvanced/getInvoiceUnit/getDefaultInvoiceFromId,
 *              private helpers: _setInvoiceUserFiscalProperties,
 *              _applyInvoiceNumber (plain generator shared with invNumber),
 *              _configureModelStatus, _initializeModelRelations,
 *              _applyModelSpecificLogic, _openCreationModal, _buildModalTitle
 *              (classic title) / _buildNuovaInvoiceTitle («UI nuova» invoice
 *              title: org name of the record's document_type + gender agreement)
 *   uses:      store, intl, session, notify, indexContext, appState, modal
 *              services; lib/utility (setDefaultCustomField, beebeeAjaxGet,
 *              notifyUserError, safeTimeZone); lib/invoice-taxes
 *              (pickInvoiceTaxes); ember-inflector (singularize);
 *              moment-timezone; lib/get-config
 *   siblings:  -
 *   tests:     new-record-factory-test.js, new-record-factory-wave4-test.js,
 *              new-record-factory-wave5-test.js, new-record-factory-wave6-test.js
 *   M14 INV-L1: «UI nuova» invoices — _openPendingModal pushes the form FRAME
 *              (invoice-edit-pending-nuova, POJO item) before the issuer /
 *              numbering requests; _openCreationModal swaps it in place for the
 *              real form (modal.replace); closed meanwhile = cancel
 *              (lastCreationCancelled, the draft is unloaded, returns null);
 *              any other exit removes the frame (finally)
 *   gotchas:   createNewModel is a @dropTask — a second call while one is
 *              running is silently dropped, not queued; catches its own errors
 *              via notifyUserError and returns null rather than rejecting
 *
 * Mixin-removal Phase 1.3 (scaffold — additive, no consumer switched yet). The
 * mixin reached `applicationController.{isClinic,gdprField,modalComponents}` by
 * name; the service routes those through the Phase-0 `app-state` / `modal`
 * services. It stays DOM-free: the old `zfDashNew.close()` +
 * `document.body.classList.remove('dropdown-open')` become an optional
 * `closeDropdown` callback the caller supplies. Document/status context that the
 * mixin read off the host component now comes from `indexContext.controller`.
 *
 * @class NewRecordFactoryService
 * @extends Service
 */
let NewRecordFactoryService = (_class = class NewRecordFactoryService extends Service {
  constructor(...args) {
    super(...args);
    _initializerDefineProperty(this, "store", _descriptor, this);
    _initializerDefineProperty(this, "intl", _descriptor2, this);
    _initializerDefineProperty(this, "session", _descriptor3, this);
    _initializerDefineProperty(this, "notify", _descriptor4, this);
    _initializerDefineProperty(this, "indexContext", _descriptor5, this);
    _initializerDefineProperty(this, "appState", _descriptor6, this);
    _initializerDefineProperty(this, "modal", _descriptor7, this);
    // ── M14 INV-L1: the «Nuova …» form frame while the new record is prepared («UI nuova» invoices) ──────────
    // Pushed on the modal stack before the issuer / numbering requests, as a plain config whose item is a POJO
    // (no hasDirtyAttributes: closing it never asks). The pending component draws the same frame as the form
    // (title, section boxes, the save bar with its buttons disabled) with .nv-skel rows. Null in classic, for
    // other models, and when the app has no pending component.
    this.lastCreationCancelled = false;
  }
  // --------------------------------------------------------------------------
  // Invoice configuration helpers
  // --------------------------------------------------------------------------

  getDocumentType(defDocumentType) {
    return DOCUMENT_TYPE_STATUS_MAP[defDocumentType] || DOCUMENT_TYPE_STATUS_MAP.default;
  }
  getInvoiceLordo() {
    return this.session.option_field?.default_invoice_lordo || false;
  }
  getInvoiceAdvanced(defDocumentType) {
    if (DOCUMENT_TYPES_WITH_ADVANCED.includes(defDocumentType)) {
      return this.session.option_field?.default_estimate_advanced || false;
    }
    return false;
  }
  getDefaultInvoiceNouser() {
    return this.session.option_field?.default_invoice_nouser || false;
  }
  getInvoiceUnit() {
    const defaultUnit = this.appState.isClinic ? 'seduta' : 'pezzo';
    return this.session.option_field?.default_invoice_unit || defaultUnit;
  }
  getInvoiceLineDefault() {
    const name = this.session.option_field?.invoice_line_default || '';
    const regexClean = /{{\s*([^}]+)\s*}}/g;
    // M14 F3: a placeholder dropped inside brackets leaves «()» — drop the empty pair (lib/invoice-line-name)
    return withoutEmptyBrackets(name.replace(regexClean, ''));
  }
  getInvoiceLineDefaultDescription() {
    return this.session.option_field?.invoice_line_default_description || '';
  }
  getDefaultInvoiceFromId() {
    const optionField = this.session.option_field;
    const defaultFromId = optionField?.default_invoice_from;
    const defaultInvoiceLists = optionField?.default_invoice_lists;
    const currentUserId = this.session.me?.id;
    const currentUserLists = defaultInvoiceLists?.[currentUserId];
    if (Array.isArray(currentUserLists)) {
      const defaultList = currentUserLists.find(list => list.default === true);
      if (defaultList) {
        return defaultList.id;
      }
    }
    return defaultFromId;
  }
  fetchDocumentTypeNumber(dataSend) {
    return beebeeAjaxGet(`${getConfig(this).APP.urlApi}/document_type_number`, this.session.token, dataSend);
  }

  // --------------------------------------------------------------------------
  // Private helpers
  // --------------------------------------------------------------------------

  _getCurrency() {
    return this.session.option_field?.intl?.currency || DEFAULT_CURRENCY;
  }
  _createAddressFromSource(sourceAddress) {
    if (sourceAddress) {
      const attributeNames = Array.from(sourceAddress.constructor.attributes.keys());
      const attributes = sourceAddress.getProperties(...attributeNames);
      return this.store.createRecord('address', attributes);
    }
    return this.store.createRecord('address', {});
  }
  _setInvoiceUserProperties(newModel, user) {
    newModel.setProperties({
      user,
      society_first_name: user.first_name,
      society_last_name: user.last_name,
      society_piva: user.piva,
      society_cf: user.cod_fiscale,
      society_piva_name: user.piva_name,
      fisica_giuridica: user.fisica_giuridica,
      society: user.ragione_sociale
    });
  }

  /**
   * Fiscal fields that belong to the emitter (`user`) rather than to the
   * document itself. Split out of `_setInvoiceBaseProperties` so a draft can be
   * re-pointed at another emitter without resetting document-level choices
   * (lordo/status/advanced/document_type) the caller may already have set.
   */
  _setInvoiceUserFiscalProperties(newModel, user) {
    const invoiceSetting = user.user_invoice_setting;
    newModel.setProperties({
      bank_name: invoiceSetting?.bank_name,
      bank_iban: invoiceSetting?.bank_iban,
      bank_bic: invoiceSetting?.bank_bic,
      rea: invoiceSetting?.rea,
      regime_fiscale: invoiceSetting?.regime_fiscale,
      notes: user.invoice_notes,
      ts_send: invoiceSetting?.ts_enable,
      ts_enable: invoiceSetting?.ts_enable
    });
  }
  _setInvoiceBaseProperties(newModel, user, defDocumentType) {
    newModel.setProperties({
      lordo: this.getInvoiceLordo(),
      status: this.getDocumentType(defDocumentType),
      advanced: this.getInvoiceAdvanced(defDocumentType),
      xml_document_type: DEFAULT_XML_DOCUMENT_TYPE,
      document_type: defDocumentType,
      valuta: this._getCurrency()
    });
    this._setInvoiceUserFiscalProperties(newModel, user);
  }
  _createDefaultInvoiceProduct(user) {
    const product = this.store.createRecord('invoiceproduct', {
      name: this.getInvoiceLineDefault(),
      description: this.getInvoiceLineDefaultDescription(),
      price: null,
      unit: this.getInvoiceUnit(),
      cost: 0,
      qta: 1,
      rank: 1
    });
    if (user.user_taxes) {
      const ivaRate = user.user_taxes.find(u => u.is_iva === true);
      if (ivaRate) {
        product.setProperties({
          iva_rate: ivaRate.rate,
          iva_natura: ivaRate.iva_natura
        });
      }
      const hasActiveRitenuta = user.user_taxes.some(u => u.is_ritenuta === true && u.is_active_default);
      if (hasActiveRitenuta) {
        set(product, 'ritenuta', true);
      }
    }
    return product;
  }
  _configureRivalsaAndBollo(newModel, user) {
    if (!user.user_taxes) return;
    const hasActiveRivalsa = user.user_taxes.some(u => u.is_rivalsa === true && u.is_active_default);
    if (hasActiveRivalsa) {
      set(newModel, 'rivalsa', true);
    }
    const bollo = user.user_taxes.find(u => u.is_bollo === true);
    if (bollo) {
      newModel.setProperties({
        bollo: true,
        bollo_cliente: bollo.bollo_cliente,
        bollo_virtuale: bollo.bollo_virtuale
      });
    }
  }
  *_configureInvoiceStyle(newModel, user) {
    if (!user.invoice_styles) return;
    const invoiceStyles = yield user.invoice_styles;
    const defaultStyle = invoiceStyles.find(u => u.is_default === true);
    if (defaultStyle) {
      set(newModel, 'invoice_style', defaultStyle);
    }
  }
  _buildModalTitle(modal, defDocumentType) {
    const organization = this.session.me;
    const newLabel = this.intl.t('new');
    let entityName;
    if (modal === 'invoice' && defDocumentType) {
      entityName = organization.get(`organization.${defDocumentType}_name`);
    } else if (modal === 'invoice') {
      entityName = this.indexContext.controller?.model?.modelName;
    } else {
      entityName = organization.get(`organization.${modal}_name`);
    }
    return `${newLabel} ${entityName}`;
  }

  /**
   * «UI nuova» title of a new-document popup (M11 W3, finding J2/B3). The classic title above builds
   * `t('new') + name` and, when the list opens the popup without a document type, `name` is the raw model
   * name — «nuovo invoice» — and the masculine adjective never agrees («nuovo Fattura»). Here the name is
   * the organization's own name of the type the factory gave the record (`document_type`, set by
   * completeTypeInvoice from the list context), lower case, with the Italian agreement of nv.list.new_named
   * («Nuova fattura», «Nuovo preventivo»). Returns null outside invoices / outside the new UI, so the
   * caller keeps the classic string byte-identical.
   */
  _buildNuovaInvoiceTitle(modal, defDocumentType, newModel) {
    if (modal !== 'invoice') return null;
    const ui = getOwner(this)?.lookup?.('service:ui');
    if (!ui?.isNuova) return null;
    const type = defDocumentType || newModel?.document_type || null;
    return this._nuovaTitleFor(this._nuovaInvoiceNoun(type));
  }

  /** Clinic, new UI: the product popup creates a treatment («Nuovo trattamento», not «Nuovo Clinica»). */
  _buildNuovaProductTitle(modal) {
    if (modal !== 'product' || !getConfig(this)?.APP?.isClinic) return null;
    const ui = getOwner(this)?.lookup?.('service:ui');
    return ui?.isNuova ? this.intl.t('nv.product.list.new_clinic') : null;
  }
  _nuovaInvoiceNoun(type) {
    let noun = null;
    if (type === 'autofattura') {
      noun = this.intl.t('nv.invoice.list.noun_selfinvoice');
    } else if (type) {
      const name = this.session.me?.get?.(`organization.${type}_name`);
      noun = typeof name === 'string' && name.trim() ? name.trim().toLowerCase() : null;
    }
    return noun || this.intl.t('nv.invoice.list.noun_document');
  }
  _nuovaTitleFor(noun) {
    // Italian agreement: a first word ending in -a is feminine (fattura, ricevuta, nota di credito,
    // autofattura, parcella); preventivo / ordine / contratto / ddt / documento are masculine.
    const gender = /a$/.test(noun.split(/\s+/)[0]) ? 'f' : 'm';
    return this.intl.t('nv.list.new_named', {
      gender,
      noun
    });
  }

  // --------------------------------------------------------------------------
  // Tasks
  // --------------------------------------------------------------------------

  *completeTypeInvoice(newModel, defDocumentTypeArgs) {
    const meId = this.session.me.id;
    let user = this.store.peekRecord('contact', meId);
    const defaultFromId = this.getDefaultInvoiceFromId();
    if (defaultFromId) {
      user = this.store.peekRecord('contact', defaultFromId);
      const needsReload = !user?.user_invoice_setting?.regime_fiscale || !user?.addresses?.[0]?.id;
      if (needsReload) {
        user = yield this.store.findRecord('contact', defaultFromId, {
          reload: true
        });
      }
    }
    const defDocumentType = defDocumentTypeArgs || this.indexContext.controller?.model?.documentType;
    if (!this.getDefaultInvoiceNouser()) {
      this._setInvoiceUserProperties(newModel, user);
    }
    this._setInvoiceBaseProperties(newModel, user, defDocumentType);
    const product = this._createDefaultInvoiceProduct(user);
    this._configureRivalsaAndBollo(newModel, user);
    yield* this._configureInvoiceStyle(newModel, user);
    newModel.invoiceproducts.push(product);
    const sourceAddress = user.addresses?.find(a => a.is_invoice === true) || user.addresses?.[0];
    const societyAddress = this._createAddressFromSource(sourceAddress);
    set(newModel, 'society_address', societyAddress);
  }
  *invNumber(newInvoice, defDocumentType, userIdOverride = null) {
    yield* this._applyInvoiceNumber(newInvoice, defDocumentType, userIdOverride);
  }

  /**
   * Plain generator behind `invNumber`. `applyInvoiceUser` delegates to it with
   * `yield*` instead of performing the task: `invNumber` is @restartableTask on
   * a singleton service, so a concurrent perform would cancel the yielded child
   * and take its parent down with it (self-cancelling-task bug class).
   */
  *_applyInvoiceNumber(newInvoice, defDocumentType, userIdOverride = null) {
    const defaultFromId = userIdOverride ?? this.getDefaultInvoiceFromId();
    const dataSend = {
      user_id: defaultFromId,
      document_type: defDocumentType || this.indexContext.controller?.model?.documentType
    };
    const data = yield this.fetchDocumentTypeNumber(dataSend);
    if (!Array.isArray(data) || data.length === 0) return;
    if (data.length > 1) {
      set(newInvoice, 'sezionali', data);
    }
    const defaultSezionale = data.find(i => i.is_default === true) || data[0];
    set(newInvoice, 'sezionale', defaultSezionale);
    set(newInvoice, 'draft_number', defaultSezionale.number || 1);
  }

  /**
   * Re-point an already-created invoice draft at a specific emitter.
   *
   * The ski-school line generators (`scuolasci_genera_fattura|ricevuta|saldo`)
   * may return a `user_id` on the returned lines: the document must then be
   * issued by that user instead of the org default (`default_invoice_from`).
   * Everything emitter-derived is re-applied — society data, fiscal settings,
   * rivalsa/bollo, invoice style, society address and the sezionale/number,
   * which is fetched again for the new emitter.
   *
   * @param {Model} newInvoice - invoice draft built by `createNewModel`
   * @param {string|number} userId - emitter contact id returned by the backend
   * @param {string|null} defDocumentType
   */
  *applyInvoiceUser(newInvoice, userId, defDocumentType = null) {
    if (!newInvoice || userId === null || userId === undefined) return;
    if (newInvoice.isDestroyed || newInvoice.isDestroying) return;

    // Ember-Data ids are strings — never `===` a raw backend number (id
    // strict-compare bug class).
    const emitterId = String(userId);
    if (String(newInvoice.user?.id ?? '') === emitterId) return;
    let user = this.store.peekRecord('contact', emitterId);
    const needsReload = !user?.user_invoice_setting?.regime_fiscale || !user?.addresses?.[0]?.id;
    if (needsReload) {
      user = yield this.store.findRecord('contact', emitterId, {
        reload: true
      });
    }
    if (!user) return;
    // The draft can be torn down (modal closed) while the contact is fetched.
    if (newInvoice.isDestroyed || newInvoice.isDestroying) return;
    if (!this.getDefaultInvoiceNouser()) {
      this._setInvoiceUserProperties(newInvoice, user);
    }
    this._setInvoiceUserFiscalProperties(newInvoice, user);

    // Clear the previous emitter's tax flags: `_configureRivalsaAndBollo` only
    // ever turns them on, so a user without rivalsa/bollo would otherwise
    // inherit them from the default emitter.
    newInvoice.setProperties({
      rivalsa: false,
      bollo: false,
      bollo_cliente: false,
      bollo_virtuale: false
    });
    this._configureRivalsaAndBollo(newInvoice, user);
    yield* this._configureInvoiceStyle(newInvoice, user);
    // `invoice_styles` is an async relation — re-check after that yield.
    if (newInvoice.isDestroyed || newInvoice.isDestroying) return;
    const sourceAddress = user.addresses?.find(a => a.is_invoice === true) || user.addresses?.[0];
    set(newInvoice, 'society_address', this._createAddressFromSource(sourceAddress));
    if (!this.getDefaultInvoiceNouser()) {
      // Drop the previous emitter's sezionali: the number fetch only writes the
      // list when the new emitter has more than one.
      set(newInvoice, 'sezionali', null);
      yield* this._applyInvoiceNumber(newInvoice, defDocumentType || newInvoice.document_type, emitterId);
    }
  }

  /**
   * Does the emitter of `invoice` withhold a ritenuta d'acconto by default?
   *
   * The ski-school line generators (`scuolasci_genera_fattura|ricevuta|saldo`)
   * return `iva_rate` / `iva_natura` / `is_bollo` per line but never a
   * `ritenuta` flag, so a generated document came out without the withholding
   * a manually added line gets from `base-invoiceline` — the invoice then
   * showed the full amount as due.
   *
   * The rule itself is not restated here: `pickInvoiceTaxes` is the single
   * source for it (ungated, like every other line-level caller). Only the
   * ritenuta is returned — the IVA of a generated line belongs to the backend
   * payload, not to the emitter's default.
   *
   * @param {Model} invoice - invoice draft, already pointed at its emitter
   * @returns {boolean} true when the emitter has an active ritenuta default
   */
  *emitterRitenutaDefault(invoice) {
    // `user` is only written onto the draft when the org shows the emitter on
    // the document (`default_invoice_nouser` off) — the taxes, though, come
    // from that same emitter either way, so fall back to the org default id.
    const emitterId = invoice?.user?.id ?? this.getDefaultInvoiceFromId() ?? this.session.me?.id;
    if (!emitterId) return false;
    const emitter = invoice?.user ?? this.store.peekRecord('contact', emitterId);
    let taxes = emitter?.user_taxes;

    // `user_taxes` is a sync (async:false) hasMany: it comes back EMPTY on a
    // contact the store only knows from a list query (list-peek stale hasMany),
    // so an emitter WITH a ritenuta configured would look like one without.
    if (!taxes?.length) {
      const reloaded = yield this.store.findRecord('contact', emitterId, {
        reload: true
      });
      if (invoice?.isDestroyed || invoice?.isDestroying) return false;
      taxes = reloaded?.user_taxes;
    }
    return pickInvoiceTaxes(taxes, this.session?.option_field, {
      gated: false
    }).ritenuta === true;
  }

  /**
   * Create a new record with the org's default configuration.
   * @param {string|null} defModal - model name (defaults to the index model)
   * @param {string|null} defDocumentType
   * @param {Object|null} defStatus
   * @param {boolean} openModal - open the edit modal afterwards
   * @param {Function|null} closeDropdown - optional caller callback to close the
   *   "new" dropdown (replaces the mixin's DOM-coupled `zfDashNew.close()` +
   *   `document.body.classList.remove('dropdown-open')`).
   * @returns {Model|null}
   */
  *createNewModel(defModal = null, defDocumentType = null, defStatus = null, openModal = true, closeDropdown = null) {
    // M14 INV-L1 («UI nuova»): a new invoice waits for the issuer reload + numbering (4-6 s on the remote API)
    // before its form can mount. Show the form frame at once; it is swapped in place for the real form below.
    this.lastCreationCancelled = false;
    let pending = null;
    let opened = false;
    let created = null;
    try {
      const modal = defModal || singularize(this.indexContext.controller?.model?.modelName);
      pending = openModal ? this._openPendingModal(modal, defDocumentType) : null;
      const me = this.store.peekRecord('contact', this.session.me.id);
      const timezone = safeTimeZone(me.time_zone);
      const newModel = created = this.store.createRecord(modal, {
        start_date: moment().tz(timezone, true).startOf('hour').format('YYYY-MM-DDTHH:mmZ'),
        end_date: moment().tz(timezone, true).add(1, 'hours').startOf('hour').format('YYYY-MM-DDTHH:mmZ'),
        date: moment().tz(timezone, true).format('YYYY-MM-DDTHH:mmZ'),
        sent: false,
        success: true
      });
      yield* this._configureModelStatus(newModel, modal, defStatus);
      yield setDefaultCustomField(me, newModel, this.store);
      yield* this._initializeModelRelations(newModel, me);
      yield* this._applyModelSpecificLogic(newModel, modal, defDocumentType, me);
      if (closeDropdown) {
        closeDropdown();
      }
      if (openModal) {
        if (pending && !this.modal.has(pending)) {
          // the user closed the frame while it loaded: that is a cancel, not an error
          this.lastCreationCancelled = true;
          return null;
        }
        this._openCreationModal(newModel, modal, defDocumentType, pending);
        opened = true;
      }
      return newModel;
    } catch (e) {
      notifyUserError(this.notify, e, this.intl);
      return null;
    } finally {
      if (pending && !opened) {
        this.modal.remove(pending);
        // nobody will ever see or save this draft: drop it (and only it) from the store
        try {
          if (created?.isNew && !created.isDestroyed && !created.isDestroying) created.unloadRecord();
        } catch {
          // already gone
        }
      }
    }
  }
  _openPendingModal(modal, defDocumentType) {
    if (modal !== 'invoice') return null;
    const owner = getOwner(this);
    if (!owner?.lookup?.('service:ui')?.isNuova) return null;
    if (!owner.factoryFor?.('component:invoice-edit-pending-nuova')) return null;
    const type = defDocumentType || this.indexContext.controller?.model?.documentType || null;
    const noun = this._nuovaInvoiceNoun(type);
    const isProForma = this.getDocumentType(type) === 'draft';
    const issueLabel = this.intl.t('nv.invoice.edit.issue', {
      noun
    });
    const draftLabel = type === 'estimate' ? this.intl.t('save draft') : this.intl.t('save fro forma');
    const cap = t => typeof t === 'string' && t ? t.charAt(0).toUpperCase() + t.slice(1) : t;
    const config = {
      title: this._buildNuovaInvoiceTitle(modal, type, null) ?? this._buildNuovaProductTitle(modal) ?? this._buildModalTitle(modal, type),
      componentName: 'invoice-edit-pending-nuova',
      item: {
        nvPending: true,
        isProForma,
        issueLabel,
        draftLabel,
        // the save-bar consequence line the form shows (invoice-edit-nuova issueHint), so the bar keeps its height
        status: isProForma ? this.intl.t(type === 'estimate' || type === 'order' ? 'nv.invoice.edit.issue_hint_wait' : 'nv.invoice.edit.issue_hint', {
          issue: issueLabel,
          draft: cap(draftLabel)
        }) : null
      }
    };
    this.modal.open(config);
    return config;
  }
  *_configureModelStatus(newModel, modal, defStatus) {
    const status = this.indexContext.controller?.status;
    if (defStatus) {
      const defStatusId = defStatus?.id || defStatus;
      const storeStatus = this.store.peekRecord(`${modal}_state`, defStatusId);
      if (storeStatus) {
        set(newModel, `${modal}_state`, storeStatus);
      }
      yield timeout(UI_STABILIZATION_DELAY);
    } else if (status && MODELS_WITH_STATUS.includes(modal)) {
      const resolvedStatus = typeof status === 'object' ? status : this.store.peekRecord(`${modal}_state`, status);
      if (resolvedStatus) {
        set(newModel, `${modal}_state`, resolvedStatus);
      }
    }
  }
  *_initializeModelRelations(newModel, me) {
    const contacts = yield newModel.contacts;
    const addresses = yield newModel.addresses;

    // dual receiver — resolved hasMany may pass Array.isArray in modern ED; native push works for both
    if (Array.isArray(contacts)) {
      contacts.push(me);
    }
    if (Array.isArray(addresses)) {
      addresses.push(this.store.createRecord('address', {
        is_invoice: true
      }));
    }
  }
  *_applyModelSpecificLogic(newModel, modal, defDocumentType, me) {
    switch (modal) {
      case 'invoice':
        set(newModel, 'date', moment().format('YYYY-MM-DD'));
        yield this.completeTypeInvoice.perform(newModel, defDocumentType);
        if (!this.getDefaultInvoiceNouser()) {
          yield this.invNumber.perform(newModel, defDocumentType);
        }
        break;
      case 'expense':
        {
          set(newModel, 'valuta', this._getCurrency());
          set(newModel, 'date', moment().format('YYYY-MM-DD'));
          const newArticle = this.store.createRecord('expensearticle', {
            first_rate: DEFAULT_EXPENSE_IVA_RATE,
            first_rate_included: true
          });
          newModel.expensearticles.push(newArticle);
          break;
        }
      case 'event':
        this._configureEventColor(newModel, me);
        break;
      case 'contact':
        this._initializeContactConsents(newModel);
        break;
    }
  }
  _configureEventColor(newModel, me) {
    const calendarSettings = this.session.option_field?.calendar_settings;
    const useSpecialistColor = calendarSettings?.default_color === 'specialista';
    if (useSpecialistColor && me.color) {
      set(newModel, 'color', me.color);
    }
  }
  _initializeContactConsents(newModel) {
    const termOptions = this.appState.gdprField?.organization_term_options?.data;
    if (!Array.isArray(termOptions)) return;
    termOptions.forEach(consent => {
      const consentRecord = this.store.createRecord('contact-consensi', {
        option_name: consent.name,
        accepted: null
      });
      newModel.contact_consensis.push(consentRecord);
    });
  }

  /**
   * Push the creation modal onto the stack via the `modal` service (DOM-free —
   * the dropdown-class toggling the mixin did here is now the caller's job).
   */
  _openCreationModal(newModel, modal, defDocumentType, pending = null) {
    const modalConfig = {
      title: this._buildNuovaInvoiceTitle(modal, defDocumentType, newModel) ?? this._buildNuovaProductTitle(modal) ?? this._buildModalTitle(modal, defDocumentType),
      componentName: this.session.me.get(`role.application.${modal}_edit`),
      item: newModel
    };
    // M14 INV-L1: the frame shown while loading is replaced at the same stack position (no close / reopen flash)
    if (!pending || !this.modal.replace(pending, modalConfig)) {
      this.modal.open(modalConfig);
    }
    // Cosmetic dropdown close the mixin did here (DOM-global). The component's
    // own `zfDashNew.close()` is still the caller's job via `closeDropdown`.
    if (typeof document !== 'undefined') {
      document.body.classList.remove('dropdown-open');
    }
  }

  /**
   * Show the modal to create a new item. Mirrors the mixin's `showNew` action.
   */
  showNew(defModal = null, defDocumentType = null) {
    return this.createNewModel.perform(defModal, defDocumentType);
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "store", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "intl", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "session", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "notify", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor5 = _applyDecoratedDescriptor(_class.prototype, "indexContext", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor6 = _applyDecoratedDescriptor(_class.prototype, "appState", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor7 = _applyDecoratedDescriptor(_class.prototype, "modal", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "completeTypeInvoice", [restartableTask], Object.getOwnPropertyDescriptor(_class.prototype, "completeTypeInvoice"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "invNumber", [restartableTask], Object.getOwnPropertyDescriptor(_class.prototype, "invNumber"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "applyInvoiceUser", [task], Object.getOwnPropertyDescriptor(_class.prototype, "applyInvoiceUser"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "emitterRitenutaDefault", [task], Object.getOwnPropertyDescriptor(_class.prototype, "emitterRitenutaDefault"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "createNewModel", [dropTask], Object.getOwnPropertyDescriptor(_class.prototype, "createNewModel"), _class.prototype), _class);

export { NewRecordFactoryService as default };
//# sourceMappingURL=new-record-factory.js.map
